// Section catalog for the App Studio editor's "Sections" palette tab.
//
// A "section" is a pre-built, drop-and-go bundle: dropping one creates a member-scoped data
// source + a configured List block + sensible field mappings in a single action (see
// AppEditor.addSection). Everything stays editable in the inspector afterward. Unlike the raw
// "Blocks" palette, a section requires no manual data-source/field wiring to produce a working,
// row-scoped view.
//
// This module is intentionally pure (no Vue) so the catalog and the moat-token helpers can be
// unit-tested without mounting a component.

// The server-enforced moat tokens. A data source carrying a contact_id rule with one of these
// tokens scopes every row to the authenticated member at runtime (RowRestrictionResolver).
// 'own' binds to the member's own contact; 'company' widens to every contact under their empresa
// (role-aware, resolved server-side). These are the single source of truth — BlockInspector.vue
// imports them too.
export const MEMBER_CONTACT_TOKEN = '{{current_member.contact_id}}';
export const MEMBER_COMPANY_TOKEN = '{{current_member.scoped_contact_ids}}';

// Both tokens, for callers that need to detect "is this rule member-scoped?".
export const MEMBER_SCOPE_TOKENS = [MEMBER_CONTACT_TOKEN, MEMBER_COMPANY_TOKEN];

// Builds the CustomFilter-shaped row_restriction rule that scopes a data source to the current
// member. `scope` is 'own' (default — the member's own contact) or 'company' (their whole empresa).
export function memberRestrictionRule(scope = 'own') {
  return {
    attribute_key: 'contact_id',
    filter_operator: 'equal_to',
    values: [scope === 'company' ? MEMBER_COMPANY_TOKEN : MEMBER_CONTACT_TOKEN],
  };
}

// One catalog entry per entity the source backbone supports. `source_model` mirrors the server
// source-adapter registry (AppData::Sources::Registry); `field_mapping` keys reference each
// adapter's serialized payload fields (app/services/app_data/sources/*_source.rb) so the dropped
// List block renders meaningful rows out of the box.
export const SECTION_TEMPLATES = [
  {
    id: 'tickets',
    source_model: 'Ticket',
    keyPrefix: 'tickets',
    label: 'Tickets',
    // Ticket-native renderer (AGORA-like list + read-only board), not the generic list block.
    block_type: 'ticket_list',
    // Drives a companion detail page so list rows click through to a full ticket view.
    detail_block_type: 'ticket_detail',
    labelKey: 'APP_BUILDER.PALETTE.SECTION_TICKETS',
    descKey: 'APP_BUILDER.PALETTE.SECTION_TICKETS_DESC',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    // The ticket_list renderer knows the Ticket payload shape directly; field_mapping is kept
    // so the block still renders sensibly if a builder switches it to a generic list.
    field_mapping: {
      title: 'title',
      subtitle: 'client_name',
      status: 'status',
      date: 'created_at',
      tag: 'priority',
    },
  },
  {
    id: 'contracts',
    source_model: 'Contract',
    keyPrefix: 'contracts',
    label: 'Contracts',
    block_type: 'list',
    labelKey: 'APP_BUILDER.PALETTE.SECTION_CONTRACTS',
    descKey: 'APP_BUILDER.PALETTE.SECTION_CONTRACTS_DESC',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    field_mapping: {
      title: 'contract_number',
      subtitle: 'contract_type',
      status: 'status',
      date: 'start_date',
      tag: 'contract_type',
    },
  },
  {
    id: 'service_orders',
    source_model: 'ServiceOrder',
    keyPrefix: 'service-orders',
    label: 'Service Orders',
    block_type: 'list',
    labelKey: 'APP_BUILDER.PALETTE.SECTION_SERVICE_ORDERS',
    descKey: 'APP_BUILDER.PALETTE.SECTION_SERVICE_ORDERS_DESC',
    icon: 'M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z',
    field_mapping: {
      title: 'order_type',
      subtitle: 'observations',
      status: 'status',
      date: 'request_date',
      tag: 'order_class',
    },
  },
];

// Serializable descriptor stashed on the drag payload (dataTransfer 'section-data'). Carrying only
// the template id keeps the payload small; AppEditor.onCanvasDrop resolves it back to the full
// template before orchestrating the drop.
export function buildSectionDragData(template) {
  return { kind: 'section', id: template.id };
}

// Resolves a template id (e.g. from a drag payload) back to its catalog entry, or undefined.
export function findSectionTemplate(id) {
  return SECTION_TEMPLATES.find(template => template.id === id);
}
